"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DATABASE_LARGE_DATA_POLICY = exports.DOCUMENT_PRODUCT_HARD_LIMIT_BYTES = void 0;
/**
 * Product hard wall for one synchronized Y.js document, not a wire payload cap.
 * Other layers: backend limits.config.ts (20 MB health backstop, 25 MB update,
 * 32 MiB transport); DocPersist merged-snapshot backstop is 64 MiB.
 */
exports.DOCUMENT_PRODUCT_HARD_LIMIT_BYTES = 10_000_000;
exports.DATABASE_LARGE_DATA_POLICY = {
    rootMaterializationGuard: {
        rowHardLimit: 0,
        rationale: 'Product database rows are never materialized in the owning doc; this guard exists only to fail loud if a root-row write is attempted.',
    },
    database: {
        rowHardLimit: 125_000,
        columnHardLimit: 128,
        systemRowHardLimit: 250_000,
        systemColumnHardLimit: 256,
        // Share of the row limit from which a database shows its row counter.
        rowCounterShare: 0.8,
        rationale: 'Every user and API action keeps a database within 125,000 rows and 128 columns. DocPersist admission enforces the system limit of 250,000 rows and 256 columns, so a concurrent offline merge that lands slightly above the product limit still synchronizes.',
    },
    row: {
        encodedByteHardLimit: 768 * 1024,
        rationale: 'One row always fits into a single row shard.',
    },
    cell: {
        limits: {
            title: { unit: 'chars', limit: 2000 },
            text: { unit: 'chars', limit: 100_000 },
            'rich-text': { unit: 'chars', limit: 100_000 },
            link: { unit: 'chars', limit: 8000 },
            relation: { unit: 'links', limit: 1000 },
            'multi-select': { unit: 'options', limit: 1000 },
            member: { unit: 'members', limit: 1000 },
            attachment: { unit: 'files', limit: 100 },
        },
        rationale: 'Cells are bounded in product units per column type; numbers, dates, checkboxes, progress and system fields have no own limit.',
    },
    ui: {
        visibleRowWindowLimit: 5000,
        visibleColumnWindowLimit: 50,
        defaultPageSize: 25,
        maxPageSize: 500,
        columnPageSize: 20,
        rationale: 'Canvas, note, and fullscreen views must render bounded visible windows.',
    },
    projection: {
        resultBudgetBytes: 8 * 1024 * 1024,
    },
    // Initial settings, not measurements: decoded table parts a worker keeps
    // for later queries, and how many. A part nobody read for a minute leaves,
    // as a kept writer document does.
    snapshotReadCache: {
        bytes: 64 * 1024 * 1024,
        partLimit: 512,
        rationale: 'A worker answers repeated queries from decoded parts within a fixed memory budget.',
    },
    jobs: {
        bulkRowLimit: 5000,
        bulkColumnLimit: 128,
        automationBatchRowLimit: 5000,
        rowSearchResultLimit: 5000,
        materializationRowLimit: 5000,
        exportBatchRowLimit: 5000,
        rationale: 'Backend jobs read explicit bounded batches, not whole tables.',
    },
    ai: {
        defaultReadRowLimit: 100,
        rowLimit: 100,
        filterMaxDepth: 3,
        filterMaxPredicates: 16,
        filterMaxValues: 32,
        filterValueMaxLength: 512,
        contextRowWindowLimit: 200,
        contextSelectedRowLimit: 500,
        contextDefaultSampleRowLimit: 3,
        contextMaterializationBatchRowLimit: 100,
        contextMaterializationByteLimit: 512 * 1024,
        rationale: 'AI tools preserve bounded selected rows, filter trees and values, project required columns, and materialize them in byte-budgeted batches.',
    },
    relationGraph: {
        maxDepth: 4,
        maxNodes: 200,
        maxEdges: 400,
        maxColumnsPerHop: 32,
        rationale: 'Relation graph reads stay explicitly bounded across traversal depth, materialized nodes, emitted edges, and projected columns.',
    },
    import: {
        inlineWarningRowThreshold: 4000,
        inlineWarningColumnThreshold: 100,
        inlineBlockRowThreshold: 5000,
        inlineBlockColumnThreshold: 128,
        inlineEstimatedByteWarningThreshold: 256 * 1024,
        inlineEstimatedByteHardLimit: 512 * 1024,
        fixedColumnOverhead: 4,
        // Excel workbooks are read whole into memory; only CSV streams. Measured
        // on the productive load path (exceljs, Node 22, generated sheet with
        // 128 columns of short text, numbers and dates):
        //   10,000 rows:  7.4 MB file,  44 MB expanded, 0.5 GB heap,  3.6 s load
        //   50,000 rows: 37.5 MB file, 228 MB expanded, 2.1 GB heap, 21.6 s load
        //  125,000 rows: load aborts with V8 "Invalid string length".
        // The expanded size (sum of the declared entry sizes) tracks the heap;
        // 256 MiB keeps the measured 50k sheet inside with a 2 GB tab budget.
        // The file size refuses a workbook before any byte is read. Larger
        // sources are imported as CSV.
        excelFileHardLimitBytes: 64 * 1024 * 1024,
        excelExpandedHardLimitBytes: 256 * 1024 * 1024,
        rationale: 'Import preflight separates inline UI budgets from queued sharded import routing; the database limits bound every import. Excel workbooks load whole into memory and are bounded by file and expanded size; CSV streams.',
    },
    column: {
        // Cells of a deleted column stay in their rows this long: undo in an open
        // session then brings the column back with every value, even for rows
        // another writer touched in between. After that the next write of a row
        // removes them. Version history restores documents as a whole and does
        // not depend on this.
        deletedCellRetentionMs: 24 * 60 * 60 * 1000,
        // More distinct values belong in a relation to a table of their own.
        optionHardLimit: 1000,
        rationale: 'Deleting a column changes the definition only; leftover cells leave a row with its next write once the retention has passed. A select or multi-select column offers at most 1,000 options; only growth is refused, so a column an offline merge lifted above the limit stays editable.',
    },
    shard: {
        targetBytes: 256 * 1024,
        hardLimitBytes: 1024 * 1024,
        writeAccountingOverheadBytes: 8 * 1024,
        // A quarter of the target: shrunken shards join an order-neighbour so
        // years of deletes do not leave thousands of near-empty shard documents.
        mergeBelowBytes: 64 * 1024,
        // Value summary of a shard entry. Each key costs about 25-30 bytes in the
        // root and keys never shrink, so a column with more distinct values or
        // months than these is marked untracked instead: a summary stays bounded
        // however many rows and edits the shard sees. 16 covers the option lists
        // of typical select columns; 36 months cover three years.
        valueSetLimit: 16,
        monthSetLimit: 36,
        valueKeyLimit: 48,
        // Stored bytes a directory part of the shard list aims for. 125k rows ×
        // 128 columns are 4,033 shards with 333 B of entry each
        // (rep-dbs-bloom-ac9-1014): 6 parts, so the first row window reads one
        // part instead of the 1.34 MB list.
        directoryPartTargetBytes: 256 * 1024,
        rationale: 'Row-shard boundaries are byte-budget based.',
    },
    render: {
        kanbanGroupLimit: 12,
        kanbanRowsPerGroupLimit: 3,
        kanbanCardsPerGroupLimit: 20,
        rationale: 'Kanban keeps its initial mounted card grid below 1000 nodes; explicit show-more actions expand only the requested lane or group.',
    },
};
